import { Dayjs } from "dayjs";
import { ActivityPeriod } from "./activityPeriod.ts";
import {
  clipAllToRange,
  hoursOf,
  mergeOverlapping,
  splitByHour,
  subtractIntervals,
  TimeInterval,
} from "./timeIntervals.ts";
import { shareOf } from "./shareOf.ts";

export type TeacherTime = {
  free: TimeInterval[];
  booked: TimeInterval[];
};

export type WeeklyAvailability = {
  weekStart: Dayjs;
  bookedHours: number;
  freeHours: number;
  offeredHours: number;
};

export type AvailabilitySummary = {
  offeredHours: number;
  bookedHours: number;
  averageOfferedHoursPerWeek: number;
  bookedShare: number | null;
};

export type WeekdayHourAvailability = {
  weekdayFromSunday: number;
  hour: number;
  offeredHours: number;
  bookedHours: number;
};

function periodRange(period: ActivityPeriod): TimeInterval {
  return { start: period.from, end: period.to };
}

export function teacherTimeInPeriod({
  openSlots,
  classRooms,
  period,
}: {
  openSlots: TimeInterval[];
  classRooms: TimeInterval[];
  period: ActivityPeriod;
}): TeacherTime {
  const booked = mergeOverlapping(
    clipAllToRange(classRooms, periodRange(period)),
  );
  const openTime = mergeOverlapping(
    clipAllToRange(openSlots, periodRange(period)),
  );
  return { free: subtractIntervals(openTime, booked), booked };
}

export function weeklyAvailability(
  teacherTime: TeacherTime,
  period: ActivityPeriod,
): WeeklyAvailability[] {
  return period.weekStarts.map((weekStart) => {
    const week = { start: weekStart, end: weekStart.add(1, "week") };
    const bookedHours = hoursOf(clipAllToRange(teacherTime.booked, week));
    const freeHours = hoursOf(clipAllToRange(teacherTime.free, week));
    return {
      weekStart,
      bookedHours,
      freeHours,
      offeredHours: bookedHours + freeHours,
    };
  });
}

export function summarizeAvailability(
  weeks: WeeklyAvailability[],
): AvailabilitySummary {
  const bookedHours = weeks.reduce(
    (total, week) => total + week.bookedHours,
    0,
  );
  const offeredHours = weeks.reduce(
    (total, week) => total + week.offeredHours,
    0,
  );
  return {
    offeredHours,
    bookedHours,
    averageOfferedHoursPerWeek: weeks.length ? offeredHours / weeks.length : 0,
    bookedShare: shareOf(bookedHours, offeredHours),
  };
}

export function availabilityByWeekdayAndHour(
  teacherTime: TeacherTime,
): WeekdayHourAvailability[] {
  const cells = new Map<string, WeekdayHourAvailability>();

  function addHours(
    intervals: TimeInterval[],
    field: "offeredHours" | "bookedHours",
  ) {
    intervals.flatMap(splitByHour).forEach(({ hourStart, minutes }) => {
      const weekdayFromSunday = hourStart.day();
      const hour = hourStart.hour();
      const key = `${weekdayFromSunday}-${hour}`;
      const cell = cells.get(key) ?? {
        weekdayFromSunday,
        hour,
        offeredHours: 0,
        bookedHours: 0,
      };
      cell[field] += minutes / 60;
      cells.set(key, cell);
    });
  }

  addHours([...teacherTime.free, ...teacherTime.booked], "offeredHours");
  addHours(teacherTime.booked, "bookedHours");

  return [...cells.values()];
}
